import React from 'react'
import { Badge, BadgeProps, Text, Tooltip } from '@chakra-ui/react'

export const MIN_CALL_SCORE = 1
export const MAX_CALL_SCORE = 10

/**
 * Maps a 1-10 call quality score to a Chakra color scheme. Tracks the
 * calibration scale used by `server/src/phone-integrations/call-scorer.ts`:
 *   1-3 = poor (red), 4-6 = mediocre (yellow), 7-8 = good (green),
 *   9-10 = excellent (teal). `null` scores render as gray.
 */
export function getScoreColorScheme(score: number | null | undefined): string {
    if (score == null) return 'gray'
    if (score >= 9) return 'teal'
    if (score >= 7) return 'green'
    if (score >= 4) return 'yellow'
    return 'red'
}

/** Short human-readable bucket label for the score. */
export function getScoreLabel(score: number | null | undefined): string {
    if (score == null) return 'Not scored'
    if (score >= 9) return 'Excellent'
    if (score >= 7) return 'Good'
    if (score >= 4) return 'Mediocre'
    return 'Poor'
}

interface ScoreBadgeProps extends Omit<BadgeProps, 'colorScheme'> {
    score: number | null | undefined
    /** Optional reasoning shown in a tooltip on hover. */
    reasoning?: string | null
    /** Whether to render `<score>/10` (default) or just the bare integer. */
    showDenominator?: boolean
}

/**
 * Pill-style score readout used in the calls table and detail modal.
 * Renders an em-dash placeholder when the score hasn't been computed yet.
 */
export default function ScoreBadge({
    score,
    reasoning,
    showDenominator = true,
    ...badgeProps
}: ScoreBadgeProps) {
    if (score == null) {
        return (
            <Tooltip
                label="This call hasn't been scored yet. Scoring runs after the call ends."
                hasArrow
                placement="top"
            >
                <Text fontSize="sm" color="gray.400">
                    --
                </Text>
            </Tooltip>
        )
    }

    const badge = (
        <Badge
            colorScheme={getScoreColorScheme(score)}
            variant="subtle"
            rounded="md"
            px={2}
            py={0.5}
            fontSize="xs"
            fontWeight="semibold"
            {...badgeProps}
        >
            {showDenominator ? `${score}/${MAX_CALL_SCORE}` : score}
        </Badge>
    )

    if (!reasoning) return badge

    return (
        <Tooltip
            label={reasoning}
            hasArrow
            placement="top"
            maxW="320px"
            openDelay={200}
        >
            {badge}
        </Tooltip>
    )
}
